import apiFetch from '@/utils/apiFetch';
import { defineStore } from 'pinia';
import DefaultConfig from '@/config/default';
import { parseCharge, formatCharge, parsePgNumericToString } from '@/utils/chargeHandler';
import type {
  PhoneValidation,
  PhoneValidationResponse,
  NinValidation,
  NINValidationResponse,
  NinMatchValidation,
  NINMatchValidationResponse,
  KYCValidation,
  KYCValidationResponse,
} from '@/config/types/responses';
import type { Pagination } from '@/config/types/pagination';

// eslint-disable-next-line import/prefer-default-export
export const useRequestStore = defineStore('requests', {
  state: () => ({
    loading: false,
    error: null as string | null,
    pagination: {} as Pagination,
    phoneNumberResults: [] as PhoneValidation[],
    phoneNumberResponse: {} as PhoneValidationResponse,
    idValidationResults: [] as NinValidation[],
    idValidationResponse: {} as NINValidationResponse,
    ninMatchResults: [] as NinMatchValidation[],
    ninMatchResponse: {} as NINMatchValidationResponse,
    kycValidationResults: [] as KYCValidation[],
    kycValidationResponse: {} as KYCValidationResponse,
  }),

  actions: {
    async fetchPhoneValidations(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/phone/requests?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.phoneNumberResults = data.data.results;
        console.log(this.phoneNumberResults);
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchAdminPhoneValidations(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/admin/phone/requests?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.phoneNumberResults = data.data.results;
        console.log(this.phoneNumberResults);
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async validatePhoneValidation(msisdn: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/phone/requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ msisdn }),
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.phoneNumberResponse = data.data;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchIDValidations(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/nin/requests?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.idValidationResults = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchAdminIDValidations(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/admin/nin/requests?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.idValidationResults = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async validateNINValidation(nin: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/nin/requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ nin }),
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.idValidationResponse = data.data;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async validateNinMatchRequest(nin: string, dateOfBirth: string, cardNo: string, surname: string, givenName: string, otherName: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/nin/match/requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            nin, dateOfBirth, cardNo, surname, givenName, otherName,
          }),
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.ninMatchResponse = data.data;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async fetchNinMatchValidations(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/nin/match/requests?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.ninMatchResults = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async fetchAdminNinMatchValidations(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/admin/nin/match/requests?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.ninMatchResults = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async fetchKYCValidations(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/kyc/requests?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.kycValidationResults = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async fetchAdminKYCValidations(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/admin/kyc/requests?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.kycValidationResults = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async validateKYCRequest(identifier: string, entityType: number, identificationType: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/validations/kyc/requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ identifier, entityType, identificationType }),
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.kycValidationResponse = data.data;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    getFormattedCharge(charge: any): string {
      return parsePgNumericToString(charge);
    },
    getParsedCharge(charge: any): number {
      return parseCharge(charge).amount;
    },
  },

});
